import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  Fragment,
} from "react";
import { Link, useParams } from "react-router-dom";

import { parseISO, differenceInMonths, format, isValid } from "date-fns";

import { HeaderTitle } from "../../../../components/HeaderTitle";

import placeholderImage from "../../../../assets/jpg/no_image.jpg";

import { animalById, historyAnimalById } from "../../../../services/animals";
import { useNotification } from "../../../../hooks/useNotification";
import { IHistory, IAnimal, ITimeline } from "../../interface";
import { formatDate } from "../../../../utils/format-date";
import { useTranslate } from "../../../../hooks/useTranslate";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import Info from "../../../../components/Info";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
} from "@mui/lab";
import { Edit } from "@mui/icons-material";

const ViewAnimal = () => {
  const params = useParams();
  const { translate, currentLanguage } = useTranslate();
  const { errorNotification } = useNotification();

  const [animal, setAnimal] = useState<IAnimal>();
  const [timeline, setTimeline] = useState<ITimeline[]>([]);

  const [openHistory, setOpenHistory] = useState(false);

  const loadAnimal = useCallback(
    async (id: string) => {
      try {
        const animal = await animalById(id);
        const history = await historyAnimalById(id);
        // const breed = await loadBreed(currentLanguage.code);

        const historyFormatted: IHistory[] = history.data.map(
          ({ description, created_at, title }: IHistory) => ({
            description,
            title,
            created_at,
            created_at_formatted: formatDate(new Date(created_at)),
          })
        );

        const timeline = historyFormatted.reduce<ITimeline[]>(
          (previousValue, currentValue) => {
            const year = new Date(currentValue.created_at).getFullYear();

            const index = previousValue.findIndex((item) => item.year === year);

            if (index < 0) {
              const newTimeline: ITimeline = { year, data: [currentValue] };
              return [...previousValue, newTimeline];
            } else {
              previousValue[index].data.push(currentValue);

              return previousValue;
            }
          },
          []
        );

        if (animal.date_birth) {
          animal.months = differenceInMonths(
            new Date(),
            parseISO(animal.date_birth)
          );
          animal.date_birth_formatted = formatDate(new Date(animal.date_birth));
        }

        setTimeline(timeline);
        setAnimal(animal);
      } catch (error) {
        console.error(error);
        errorNotification(translate("animals.error_load_animal"));
      }
    },
    [errorNotification, translate]
  );

  const ready = useRef(false);

  useEffect(() => {
    if (!ready.current) {
      ready.current = true;
      if (params.id) loadAnimal(params.id);
    }
  }, [params, loadAnimal]);

  if (!animal) return null;

  return (
    <Stack spacing={5}>
      <HeaderTitle
        title={translate("animals.title_view")}
        url="/animals"
        showIcon
      />
      <Box>
        <Grid container spacing={3}>
          <Grid item md={false}>
            <Stack spacing={1}>
              <Paper
                component="img"
                height={300}
                width={350}
                src={animal.photo ?? placeholderImage}
                sx={{ objectFit: "cover" }}
              />
            </Stack>
          </Grid>
          <Grid item md>
            <Paper component={Stack} spacing={2} sx={{ p: 3, minHeight: 300 }}>
              <Typography
                component={Stack}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ fontSize: 25, fontWeight: "bold" }}
              >
                <span>{animal.name}</span>
                <Chip size="small" label={`#${animal.number_identifier}`} />
                <Tooltip title={translate("animals.title_update")}>
                  <IconButton
                    size="small"
                    sx={{ justifySelf: "end" }}
                    component={Link}
                    to={`../animals/edit/${animal.id}`}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
              </Typography>
              <Stack direction="row" spacing={5}>
                <Info
                  title={translate("animals.item_dob")}
                  value={animal.date_birth_formatted}
                />
                <Info
                  title={translate("animals.item_age")}
                  value={
                    animal.months
                      ? `${animal.months} ${translate("animals.months")}`
                      : "-"
                  }
                />
              </Stack>
              <Stack direction="row" spacing={5}>
                <Info
                  title={translate("animals.placeholder_date_weighing")}
                  value={
                    animal.date_weighing
                      ? format(parseISO(animal.date_weighing), "dd/MM/yyyy")
                      : "-"
                  }
                />
                <Info
                  title={translate("animals.placeholder_weighing")}
                  value={`${animal.weighing?.[0].weight ?? "-"}`}
                />
              </Stack>
              <Stack direction="row" spacing={5}>
                <Info
                  title={translate("animals.item_race")}
                  value={animal?.race?.language?.[currentLanguage.code] ?? "-"}
                />
                <Info
                  title={translate("animals.item_stage")}
                  value={
                    animal?.internship?.language?.[currentLanguage.code] ?? "-"
                  }
                />
              </Stack>
              <Stack direction="row" spacing={5}>
                <Info
                  title={translate("management.description")}
                  value={animal.description ?? "-"}
                />
              </Stack>
              <Stack direction="row" spacing={5}>
                <Info
                  title={translate("animals.placeholder_batch")}
                  value={animal.batch?.name ?? "-"}
                />
                <Info
                  title={translate("animals.placeholder_flock")}
                  value={animal.flock?.name ?? "-"}
                />
              </Stack>
              {timeline.length > 0 && (
                <Button
                  onClick={() => {
                    setOpenHistory((old) => !old);
                  }}
                >
                  {translate(
                    openHistory
                      ? "animals.item_close_history"
                      : "animals.item_view_history"
                  )}
                </Button>
              )}
              <Collapse in={openHistory} unmountOnExit>
                <Paper elevation={0} sx={{ bgcolor: "grey.100" }}>
                  <Timeline>
                    {timeline.map((item, index) => (
                      <Fragment key={index}>
                        <TimelineItem key={index.toString()}>
                          <TimelineOppositeContent sx={{ fontWeight: "bold" }}>
                            {item.year}
                          </TimelineOppositeContent>
                          <TimelineSeparator>
                            <TimelineDot color="primary" />
                            <TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent></TimelineContent>
                        </TimelineItem>
                        {item.data.map((history, indexItem) => (
                          <TimelineItem
                            key={index.toString() + indexItem.toString()}
                          >
                            <TimelineOppositeContent
                              sx={{ fontSize: 14, my: 0.1, color: "grey.600" }}
                            >
                              {isValid(parseISO(history.created_at)) &&
                                format(
                                  parseISO(history.created_at),
                                  "dd/MM H:mm"
                                )}
                            </TimelineOppositeContent>
                            <TimelineSeparator>
                              <TimelineDot />
                              {(index < timeline.length - 1 ||
                                indexItem < item.data.length - 1) && (
                                <TimelineConnector />
                              )}
                            </TimelineSeparator>
                            <TimelineContent>
                              <Typography
                                sx={{ fontSize: 16, fontWeight: "bold" }}
                              >
                                {history.title[currentLanguage.code]}
                              </Typography>
                              {history.description[currentLanguage.code]}
                            </TimelineContent>
                          </TimelineItem>
                        ))}
                      </Fragment>
                    ))}
                  </Timeline>
                </Paper>
              </Collapse>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
};

export { ViewAnimal };
